import { Injectable,
    Component,
    Input
} from '@angular/core';
import { TreeNode } from '../models/tree-node.model';
import { TreeOptions } from '../models/tree-options.model';
import { TREE_EVENTS } from '../constants/events';
import { DELIMITER } from '../constants/keys';
const _ = require('lodash');

@Injectable()
export class TreeService {
    static focusedTree = undefined;
    nodes: any[];
    roots: TreeNode[];
    virtualRoot: TreeNode;
    options: TreeOptions;
    activeNode: TreeNode = undefined;
    focusedNode: TreeNode = undefined;
    previousFocusedNode: TreeNode = undefined;
    eventNames = Object.keys(TREE_EVENTS);
    firstUpdate = true;
    focusedNodeId: string = undefined;
    previousFocusedNodeId: string = undefined;
    expandedNodeIds: { [id: string]: boolean } = {};
    expandedNodes: TreeNode[];
    previousExpandedNodeIds: { [id: string]: boolean } = {};
    previousExpandedNodes: TreeNode[];
    activeNodeIds: { [id: string]: boolean } = {};
    activeNodes: TreeNode[];
    private events: any;
    private _treeNodeTemplateComponent: any;
    private _loaderComponent: any;
    private idsFromUrl: any;


    setData({ nodes, options, events }) {
        if (options) {
            this.options = new TreeOptions(options);
        }
        if (events) {
            this.events = events;
        }
        if (nodes) {
            this.nodes = nodes;
        }
        this.update();
    }

    update() {
        let treeNodeConfig = { virtual: true, [this.options.childrenField]: this.nodes };
        this.virtualRoot = this.getTreeNode(treeNodeConfig, null);
        this.roots = this.virtualRoot.children;
        this.initTreeNodeTemplateComponent();
        this.initLoaderComponent();
        this.loadState();
        if (this.firstUpdate) {
            if (this.roots) {
                this.fireEvent({ eventName: TREE_EVENTS.onInit });
                this.firstUpdate = false;
                this.calculateExpandedNodes();
            } else {
                this.fireEvent({ eventName: TREE_EVENTS.onUpdate });
            }
        }
    }

    calculateExpandedNodes(startNode = null) {
        startNode = startNode || this.virtualRoot;

        if (startNode.data[this.options.isExpandedField]) {
            this.expandedNodeIds[startNode.falseId] = true;
        }
        if (startNode.children) {
            startNode.children.forEach((child) => this.calculateExpandedNodes(child));
        }
    }

    loadState() {
        if (this.focusedNodeId) {
            this.focusedNode = this.getNodeById(this.focusedNodeId);
        }
        this.expandedNodes = Object.keys(this.expandedNodeIds)
            .filter((id) => this.expandedNodeIds[id])
            .map((id) => this.getNodeById(id.split(DELIMITER)[0]));
        this.expandedNodes = _.compact(this.expandedNodes);
        this.activeNodes = Object.keys(this.activeNodeIds)
            .filter((id) => this.expandedNodeIds[id])
            .map((id) => this.getNodeById(id.split(DELIMITER)[0]));
        this.activeNodes = _.compact(this.activeNodes);
    }

    loadPreviousState() {
        this.previousExpandedNodeIds = _.cloneDeep(this.expandedNodeIds);
        this.previousExpandedNodes = Object.keys(this.previousExpandedNodeIds)
            .filter((id) => this.previousExpandedNodeIds[id])
            .map((id) => this.getNodeById(id.split(DELIMITER)[0]));
        this.previousExpandedNodes = _.compact(this.expandedNodes);
        this.previousFocusedNodeId = _.cloneDeep(this.focusedNodeId);
        if (this.previousFocusedNodeId) {
            this.previousFocusedNode = this.getNodeById(this.previousFocusedNodeId);
        }
    }

    getTreeNode(node: any, parent: TreeNode): TreeNode {
        return new TreeNode(node, parent, this);
    }

    fireEvent(event) {
        this.events[event.eventName].next(event);
    }

    getFirstRoot() {
        return _.first(this.roots);
    }

    getLastRoot() {
        return _.last(this.roots);
    }

    get isFocused() {
        return TreeService.focusedTree === this;
    }

    isNodeFocused(node) {
        return this.focusedNode === node;
    }

    isActive(node) {
        return this.activeNodeIds[node.id];
    }

    setActiveNode(node, value, multi = false) {
        if (multi) {
            this.setActiveNodeMulti(node, value);
        } else {
            this.setActiveNodeSingle(node, value);
        }
    }

    setActiveNodeSingle(node, value) {
        this.activeNodeIds = {};
        this.activeNodes = [];
        if (value) {
            this.activeNodes.push(node);
            this.activeNodeIds[node.id] = true;
        }
    }

    setActiveNodeMulti(node, value) {
        this.activeNodeIds[node.id] = value;
        if (value) {
            if (!_.includes(this.activeNodes, node)) {
                this.activeNodes.push(node);
            }
        } else {
            if (_.includes(this.activeNodes, node)) {
                _.remove(this.activeNodes, node);
            }
        }
    }

    setFocus(value) {
        TreeService.focusedTree = value ? this : undefined;
    }

    getFocusedNode(): TreeNode {
        return this.focusedNode;
    }

    setFocusedNode(node) {
        this.focusedNode = node;
        this.focusedNodeId = node ? node.id : undefined;
    }

    getActiveNode(): TreeNode {
        return this.activeNodes[0];
    }

    getActiveNodes(): TreeNode[] {
        return this.activeNodes;
    }

    get treeNodeTemplateComponent() { return this._treeNodeTemplateComponent; };

    get loaderComponent() { return this._loaderComponent; };
    initTreeNodeTemplateComponent() {
        this._treeNodeTemplateComponent = this.options.treeNodeTemplate;
        if (typeof this._treeNodeTemplateComponent === 'string') {
            this._treeNodeTemplateComponent =
                this.createWrapperComponent(this._treeNodeTemplateComponent);
        }
    }

    initLoaderComponent() {
        this._loaderComponent = this.options.loaderComponent;
        if (typeof this._loaderComponent === 'string') {
            this._loaderComponent = this.createWrapperComponent(this._loaderComponent);
        }
    }

    createWrapperComponent(templateStr) {
        @Component({
            selector: 'TreeNodeTemplate',
            template: templateStr
        })
        class WrapperComponent {
            @Input() node: TreeNode;
        }
        return WrapperComponent;
    }

    focusNextNode() {
        let previousNode = this.focusedNode;
        let nextNode = previousNode ? previousNode.findNextNode() : this.getFirstRoot();
        if (nextNode) {
            nextNode.focus();
        }
    }

    focusPreviousNode() {
        let previousNode = this.focusedNode;
        let nextNode = previousNode ? previousNode.findPreviousNode() : this.getLastRoot();
        if (nextNode) {
            nextNode.focus();
        }
    }

    focusDrillDown() {
        let previousNode = this.focusedNode;
        if (previousNode && previousNode.isCollapsed && previousNode.hasChildren) {
            previousNode.toggle();
        } else {
            let nextNode = previousNode && previousNode.getFirstChild();
            if (nextNode) {
                nextNode.focus();
            }
        }
    }

    focusDrillUp() {
        let previousNode = this.focusedNode;
        if (!previousNode) return;
        if (previousNode.isExpanded) {
            previousNode.toggle();
        } else {
            let nextNode = previousNode && previousNode.realParent;
            if (nextNode) {
                nextNode.focus();
            }
        }
    }

    onSearchComplete($event) {
        this.focusSearchedNode($event.node);
    }

    focusSearchedNode(nodeToBeSelected) {
        let topParent = this.findRoot(nodeToBeSelected);
        if (topParent && topParent.isCollapsed) {
            topParent.toggle();
        }
        topParent.focus();
        for (let i = 1; i < nodeToBeSelected.path.length - 1; i++) {
            let node = this.findNode(nodeToBeSelected.path[i], topParent);
            if (node && node.isCollapsed) {
                node.toggleExpanded();
            }
            if (node) {
                node.focus();
            }
        }
        nodeToBeSelected.focus();
        this.setFocus(false);
    }

    findRoot(node) {
        if (node.level === 1) {
            return node;
        } else if (node.level !== 0 ) {
            let parent = node.parent;
            return this.findRoot(parent);
        }
    }

    findNode(id, currentNode) {
        if (!currentNode) {
            currentNode = this.virtualRoot;
        }
        if (currentNode.id && id === currentNode.id) {
            return currentNode;
        } else {
            for (let index in currentNode.children) {
                if ( currentNode.children[index] ) {
                    let node = currentNode.children[index];
                    if (node.id === id) {
                        return node;
                    }
                    this.findNode(id, node);
                }
            }
            return undefined;
        }
    }

    findNodeById(id, currentNode) {
        if (!currentNode) {
            currentNode = this.virtualRoot;
        }
        if (currentNode.idField && id === currentNode.idField) {
            return currentNode;
        } else {
            for (let index in currentNode.children) {
                if ( currentNode.children[index] ) {
                    let node = currentNode.children[index];
                    if (node.data.idField === id) {
                        this.fireEvent({eventName: TREE_EVENTS.onSearchComplete, node: node });
                        return node;
                    }
                    this.findNodeById(id, node);
                }
            }
            return undefined;
        }
    }

    findNodeByName(name, currentNode) {
        if (!currentNode) {
            currentNode = this.virtualRoot;
        }
        if (currentNode.name && name === currentNode.name) {
            return currentNode;
        } else {
            for (let index in currentNode.children) {
                if ( currentNode.children[index] ) {
                    let node = currentNode.children[index];
                    if (node.data.name.toLowerCase() === name.toLowerCase()) {
                        this.fireEvent({eventName: TREE_EVENTS.onSearchComplete, node: node });
                        return node;
                    }
                    this.findNodeByName(name, node);
                }
            }
            return undefined;
        }
    }
    getNodeByPath(path, startNode = null): TreeNode {
        if (!path) {
            return null;
        }

        startNode = startNode || this.virtualRoot;
        if (path.length === 0) {
            return startNode;
        }

        if (!startNode.children) {
            return null;
        }

        const childId = path.shift();
        const childNode = _.find(startNode.children, { [this.options.idField]: childId });

        if (!childNode) {
            return null;
        }

        return this.getNodeByPath(path, childNode);
    }

    getNodeById(id) {
        return this.getNodeBy({ id });
    }

    getNodeByName(name) {
        return this.getNodeBy(function(o) { return o.data.displayField === name; });
    }

    getNodeBy(predicate, startNode = null) {
        startNode = startNode || this.virtualRoot;

        if (!startNode.children) {
            return null;
        }
        const found = _.find(startNode.children, predicate);
        if (found) {
            return found;
        } else {
            for (let child of startNode.children) {
                const fd = this.getNodeBy(predicate, child);
                if (fd) {
                    return fd;
                }
            }
        }
    }

    filter(value, originalNodes) {
        if (_.isEmpty(value)) {
            this.nodes = originalNodes;
        } else {
            this.nodes = _.filter(originalNodes, function(o) {
                if ( _.startsWith(o.name.toLowerCase(), value.toLowerCase()) ) {
                    return true;
                } else if (o.children) {
                    let children = _.filter(o.children, function(c) {
                        if ( _.startsWith(c.name.toLowerCase(), value.toLowerCase()) ) {
                            return true;
                        }
                    });
                    if (children.length > 0) {
                        return true;
                    }
                }
            });
        }
        return this.nodes;
    }

    isExpanded(node) {
        return this.expandedNodeIds[node.falseId];
    }

    setExpandedNode(node, value) {
        const index = _.indexOf(this.expandedNodes, node);
        if (value && !index) {
            this.expandedNodes.push(node);
        } else if (index) {
            _.pullAt(this.expandedNodes, index);
        }
        this.expandedNodeIds[node.falseId] = value;
    }

    filterNodes(filter, autoShow = false) {
        let filterFn;

        if (!filter) {
            return this.clearFilter();
        }

        if (_.isString(filter)) {
            filterFn = (node) => node.displayField.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
        } else if (_.isFunction(filter)) {
            filterFn = filter;
        } else {
            console.error('Error - should be either a string or function', filter);
        }
        this.roots.forEach((node) => node.filter(filterFn, autoShow));
    }

    clearFilter() {
        this.roots.forEach((node) => node.clearFilter());
    }

    afterFirstLevelLoad() {
            setTimeout(() => {
                this.expandedNodes.forEach((node) => {
                    if (!node.children) {
                        node.loadChildren(true);
                    }
                });
            });
    }

    prepareRefresh() {
        this.loadPreviousState();
        this.setFocusedNode(null);
    }

    selectFirstRoot() {
        let root = this.getFirstRoot();
        root && root.toggleActivated();
    }

    setIdsFromUrl(ids: any) {
        this.idsFromUrl = ids;
    }
    getIdsFromUrl() {
        return this.idsFromUrl;
    }

    onLoadChildren(event) {
        if (event.refresh) {
            let previous;
            this.setFocusedNode(undefined);
            if (this.previousFocusedNodeId) {
                previous = this.getNodeById(this.previousFocusedNodeId);
                if (previous) {
                    this.setFocusedNode(previous);
                    previous.focus();
                    previous.activated();
                } else if (this.previousFocusedNode && (event.node.id === this.previousFocusedNode.parent.id)) {
                    this.selectFirstRoot();
                }
            }
        }
        let idsFromUrl = this.getIdsFromUrl();
        if (this.idsFromUrl) {
            let node = this.getNodeById(event.node.id);
            let pos: number = this.idsFromUrl.ids.indexOf(event.node.id);
            let idsLength: number = this.idsFromUrl.ids.length;
            if (!node || pos === -1) {
                return;
            }
            if (this.idsFromUrl.item === "compute") {
                if (pos === (idsLength - 1)) {
                    node.blur();
                    node.focus();
                } else if (pos < (idsLength - 1)) {
                    node.expand();
                }
                return;
            }

            if (this.idsFromUrl.item !== "compute") {

            }
        }
    }

}



// WEBPACK FOOTER //
// ./src/common/tree/services/tree.service.ts